import type { Subscription } from 'effector';
import type { AutoRotateItem } from '@types';
import { $loadedScene } from '@stores/loadedStore';
import { MainPlayer } from '@stores/krpPlayerStores';
import { rotateToPromise } from '../utils/rotateLookTo';
import { onUserInteractive } from '../../player/actions/onUserInteractive';

type QueueItem = Pick<
  AutoRotateItem,
  'hlookat' | 'vlookat' | 'fov' | 'duration'
> & {
  nonblocking?: true;
};

const store: {
  isOn: boolean;
  queue: QueueItem[];
  pauseItem: QueueItem | null;
  curIndex: number;
} = {
  isOn: false,
  queue: [],
  pauseItem: null,
  curIndex: 0,
};

const resume = async () => {
  let timeId: ReturnType<typeof setTimeout>;
  let unwatch: Subscription | undefined = undefined;
  const fn = () => {
    clearTimeout(timeId);
    timeId = setTimeout(async () => {
      if (!store.pauseItem || !store.isOn) return;
      await rotateToPromise({
        ...store.pauseItem,
        nonblocking: true,
        duration: 0.2,
      });
      unwatch?.();
      play(store.curIndex, { duration: store.pauseItem.duration });
    }, 3000);
  };
  unwatch = onUserInteractive.watch(() => {
    fn();
  });
  fn();
};

const play = async (index: number, opts: { duration?: number } = {}) => {
  if (!store.isOn) return;
  store.curIndex = index;
  const item = store.queue[index];
  if (item) {
    const startTime = Date.now();
    const { isDone } = await rotateToPromise({
      ...item,
      nonblocking: true,
      ...(opts.duration && { duration: opts.duration }),
    });
    if (isDone) {
      play(index + 1);
    } else {
      MainPlayer.get()?.stopLookTo();
      const costTime = Math.min((Date.now() - startTime) / 1000, item.duration);
      const view = MainPlayer.get()?.getView()!;
      store.pauseItem = {
        fov: view.fov,
        hlookat: view.hlookat,
        vlookat: view.vlookat,
        duration: item.duration - costTime,
      };
      resume();
    }
  } else {
    play(0);
  }
};

const getQueue = (list: AutoRotateItem[]): QueueItem[] => {
  const arr: QueueItem[] = [];
  const scene = $loadedScene.getState();
  if (!scene) return arr;
  arr.push({
    hlookat: scene.view.hLookAt,
    vlookat: scene.view.vLookAt,
    fov: scene.view.fov,
    duration: 0.2,
  });
  list.forEach((x, i) => {
    const last = list[i - 1];
    if (last && x.startAt > last.startAt + last.duration) {
      arr.push({
        hlookat: last.hlookat,
        vlookat: last.vlookat,
        fov: last.fov,
        duration: x.startAt - (last.startAt + last.duration),
      });
    }
    arr.push({
      hlookat: x.hlookat,
      vlookat: x.vlookat,
      fov: x.fov,
      duration: x.duration,
    });
  });
  return arr;
};

export const startCustomRotate = async () => {
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config?.autoRotate?.tourData ?? [];
  if (!list.length) return;
  store.isOn = true;
  store.queue = getQueue(list);
  play(0);
};

export const stopCustomRotate = () => {
  store.isOn = false;
  store.queue = [];
  store.pauseItem = null;
  store.curIndex = 0;
  MainPlayer.get()?.stopLookTo();
};
